import { Button, Tab, Tabs } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTypedParams } from 'react-router-typesafe-routes/dom';
import { AppRoutes, useNavBack } from '~/app/app.routes';
import { AuthService } from '~/service/service.auth';
import { BrandService } from '~/service/service.brand';
import { UIService } from '~/service/service.ui';
import { BrandCreateDto } from '~/shared/dto/admin/brand.dto';
import { BrandId } from '~/shared/dto/id';
import { Form } from '~/shared/form/form';
import { useService } from '~/shared/service/service.base';
import { FeedbackDrawer } from '~/view/feedback/feedback.drawer';
import { FieldImages } from '~/view/fields/field.images';
import { EditCenter, EditContainer, EditLeft, EditRight } from '~/view/view.edit';
import { BrandGeneral } from './brand.general';
import { BrandSuggestions } from './brand.suggestions';

export const BrandEditPage = observer(() => {
	const [ui, auth, brands] = useService(UIService, AuthService, BrandService);
	const navBack = useNavBack();
	const { id } = useTypedParams(AppRoutes.BrandEdit);
	const brandId = id as BrandId;
	const [tab, setTab] = useState('general' as 'general' | 'suggestions');

	const form = Form.use(BrandCreateDto, {
		name: 'BrandCreateDto',
		onSubmit: dto =>
			brands.save(id, dto).then(() => {
				navBack();
				ui.notify('Brand saved successfully', 'success');
			}),
		initial: () => brands.getOne(id),
	});

	ui.usePageTitle(form.initial?.name ? `Brand - ${form.initial?.name}` : '');
	const canSave = id ? auth.can('brand', 'UPDATE') : auth.can('brand', 'CREATE');

	return (
		<>
			<EditContainer async={brands.async}>
				<EditLeft>
					<FieldImages field={form.$images} images={form.initial?.images} />
				</EditLeft>
				<EditCenter minWidth={800}>
					<Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="main tabs" centered>
						<Tab label="General" value="general" />
						<Tab label="Suggestions" value="suggestions" />
					</Tabs>
					{tab === 'general' && <BrandGeneral id={brandId} form={form} />}
					{tab === 'suggestions' && id && <BrandSuggestions brandId={brandId} />}
				</EditCenter>
				<EditRight>
					<Button variant="contained" onClick={form.submit} disabled={!canSave}>
						Save
					</Button>
					<br />
					<Button variant="outlined" onClick={navBack}>
						Cancel
					</Button>
					<Button variant="outlined" onClick={form.reset}>
						Reset
					</Button>
					{id && (
						<>
							<Button variant="outlined" onClick={() => ui.openHistory('BRAND', id)}>
								Show history
							</Button>
							<br />
							<Button
								disabled={!auth.can('brand', 'DELETE')}
								variant="outlined"
								color="error"
								onClick={() =>
									ui.confirmDelete(() =>
										brands.delete(id).then(() => {
											navBack();
											ui.notify('Brand deleted successfully', 'success');
										}),
									)
								}
							>
								Delete
							</Button>
						</>
					)}
				</EditRight>
			</EditContainer>
			{tab === 'suggestions' && <FeedbackDrawer model={brands.suggestions.drawer} />}
		</>
	);
});
